import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Brain, Zap, Target, BarChart3, Gem, Bot, LineChart } from "lucide-react";
import { trackEvent } from "@/lib/trackEvent";
import { ReviewerSearchBar } from "@/components/home/ReviewerSearchBar";

/** Ordered by how central each tool is to daily use, not alphabetically. */
const TOOLS = [
  { to: "/buddy", icon: Bot, title: "GoToBuddy", copy: "Your AI portfolio coach — brutally honest, plain English." },
  { to: "/undervalued-gems", icon: Gem, title: "Undervalued Gems", copy: "Fundamental value screens for overlooked tokens." },
  { to: "/market-analytics", icon: BarChart3, title: "Market Analytics", copy: "Correlation matrix and relative strength vs BTC/ETH." },
  { to: "/daily-opportunities", icon: LineChart, title: "Daily Opportunities", copy: "EMA, MACD, RSI and volume screens, refreshed daily." },
];

/**
 * Zone 2 — the core tools, each appearing exactly once.
 * The AI Coin Reviewer is the single, flagship placement on the homepage.
 */
export function HomeToolsZone() {
  return (
    <div className="container mx-auto px-4 space-y-6">
      <Card className="overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-purple-500/10 to-pink-500/10">
        <CardContent className="p-6 md:p-8 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-primary text-primary-foreground gap-1">
              <Sparkles className="h-3 w-3" />
              Most used
            </Badge>
            <Badge variant="outline" className="border-primary/40 text-primary">Free</Badge>
          </div>
          <div>
            <h3 className="text-2xl md:text-4xl font-extrabold mb-3">AI Coin Reviewer</h3>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
              Paste any coin and get a full research report in about 30 seconds — technical setup, fundamental
              due diligence, sentiment, risk profile and a clear verdict.
            </p>
          </div>

          <ReviewerSearchBar bare />

          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Brain, label: "Deep Analysis" },
              { icon: Zap, label: "30-Second Result" },
              { icon: Target, label: "Trade Signals" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="text-center p-3 rounded-lg bg-background/60">
                <Icon className="w-6 h-6 mx-auto mb-2 text-primary" />
                <span className="text-xs md:text-sm text-muted-foreground">{label}</span>
              </div>
            ))}
          </div>

          <Button
            asChild
            size="lg"
            className="h-12 text-base font-bold gap-2 w-full sm:w-auto bg-gradient-to-r from-primary to-purple-500"
            onClick={() => trackEvent("coin_review_started", { source: "tools_zone" })}
          >
            <Link to="/coin-reviewer">
              Open the full reviewer
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TOOLS.map(({ to, icon: Icon, title, copy }) => (
          <Link key={to} to={to} className="group">
            <Card className="h-full border-border/60 hover:border-primary/40 transition-colors">
              <CardContent className="p-5">
                <Icon className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-bold mb-1 group-hover:text-primary transition-colors">{title}</h3>
                <p className="text-sm text-muted-foreground">{copy}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
